import * as React from "react";
import styled, { css } from "styled-components";
import { useMappedState } from "redux-react-hook";
import _ from "lodash";
import { useRouteMatch } from "react-router-dom";

import Grid from "src/library/components/Hedron";
import Link from "src/library/components/Link/Link";
import {
  Text,
  TextTruncate
} from "src/library/components/Text";
import { calculateCartTotalQuantity } from "src/core/orders/utils/orders";
import { usePriceVisibility } from "src/core/prices/hooks/use-price-visibility";
import { useDrop } from "src/core/drops/hooks";
import { formatDropName } from "src/core/drops/utils/format-drop-name";
import { ContextHeight } from "src/context/SideBarContext/utils";
import { TotalPrice } from "src/core/prices/components/TotalPrice";
import useList from "src/core/lists/hooks/useList";

import { IStore } from "../../store";

const InListWrapper = styled(Grid.Bounds)`
  ${({ theme }) => css `
    display: flex;
    justify-content: flex-end;
    align-items: center;
    flex: 1;

    .context-item {
      padding: ${theme.spacing.padding.md};
      height: ${ContextHeight}px;
      justify-content: space-between;
      display: flex;
      flex-direction: column;

      .context-item-label {
        color: ${theme.colors.accentLight};
        font-weight: ${theme.fonts.weights.bold};
        letter-spacing: 1px;
      }
    }

    .context-item-value {
      max-width: 250px;

      span {
        color: ${theme.colors.white};
      }
    }
  `}
`;

const Cart = styled(Grid.Box)`
  height: 70px;
  cursor: pointer;

  ${({ theme }) => css`
    padding: ${theme.spacing.padding.md};
    background: ${theme.colors[ "primaryDark" ]};
    color: ${theme.fonts.colors[ "white" ]};

    .totals {
      p:nth-child(1) {
        text-transform: uppercase;
        font-weight: ${theme.fonts.weights.normal};
        font-size: ${theme.fonts.sizes.p};
      }

      font-size: ${theme.fonts.sizes.h4};
      font-weight: ${theme.fonts.weights.bold};

      .current-price {
        font-weight: ${theme.fonts.weights.bold};
        color: ${theme.fonts.colors[ "white" ]};
      }
    }
  `}
`;

export const InListContext: React.ReactFCWithChildren = () => {
  const match = useRouteMatch();
  const pricesVisible = usePriceVisibility();
  const [ { currentList: { data: list } } ] = useList();
  const [ { drop } ] = useDrop();
  const { terminology } = useMappedState(({ global }: IStore) => ({ terminology: global.settings?.terminology }));

  if (!list) {
    return null;
  }

  return (
    <InListWrapper className="in-list-wrapper">
      {drop && (
        <div className="context-item drop-name-context-item">
          <Text className="context-item-label">
            {_.startCase(_.toLower(terminology?.drop.singular.text))}
          </Text>

          {/* <br /> */}

          <div className="context-item-value">
            <TextTruncate
              truncateProps={{ lines: 2 }}
              fontSize="sub"
              className="context-item-value-text"
            >
              {formatDropName(drop, true)}
            </TextTruncate>
          </div>
        </div>
      )}

      <Cart>
        <Link to={`/lists/${list.id}`}>
          {pricesVisible ? (
            <div className="totals">
              <Text
                weight="bold"
                color="white"
              >
                List Total
              </Text>

              <br />

              <Text
                weight="bold"
                color="white"
              >
                {list.listLines ? (
                  <>
                    <TotalPrice
                      lines={list.listLines}
                      id={list.id}
                      totalOnly
                    />

                    {` (${calculateCartTotalQuantity(list.listLines)} items)`}
                  </>
                ) :
                  "0 items"}
              </Text>
            </div>
          ) : (
            <div
              style={{
                width: "50px",
                textAlign: "center"
              }}
            >
              {match.path !== "/lists/:listId" ? (
                <>
                  <Text
                    weight="bold"
                    color="white"
                  >
                    View
                  </Text>

                  <br />

                  <Text
                    weight="bold"
                    color="white"
                  >
                    List
                  </Text>
                </>
              ) : null}
            </div>
          )}
        </Link>
      </Cart>
    </InListWrapper>
  );
};
